import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import {
  Search, MapPin, Flower, X, Loader2, Filter, LayoutGrid, List,
  Heart, PenLine, Share2, ChevronRight, Sparkles, ExternalLink,
  Feather, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import ObituarySubscribeStrip from "@/components/ObituarySubscribeStrip";
import ObituariesTabs from "@/components/ObituariesTabs";
import SeoHead from "@/components/SeoHead";
import MemorialBadges from "@/components/MemorialBadges";

// -----------------------------------------------------------------------------
// /obituaries — chronological archive with a Legacy.com-inspired vibrant layout.
//
// Sections rendered:
//   • Hero eyebrow + big title + soft explainer paragraph
//   • "Today's featured obituaries" horizontal-scroll carousel (top 8 with
//     photos + rich English snippets)
//   • Search box + filter bar
//     - When (All time / Today / This week / …)
//     - Source chips (Emalayalee · Manorama · Legacy · Tributes …)
//     - Country + State chips
//     - Age range chips (50-60 · 61-70 · 71-80 · 81-90 · 90+)
//   • Layout toggle (grid ↔ list)
//   • Trust-signal count "N obituaries remembered on Social Pulse"
//   • Chronological results grouped by import day, with card-inline actions
//     (Leave a memory / Follow / Share)
// -----------------------------------------------------------------------------

const AGE_RANGES = [
  { key: "",       label: "Any age", min: null, max: null },
  { key: "50-60",  label: "50–60", min: 50, max: 60 },
  { key: "61-70",  label: "61–70", min: 61, max: 70 },
  { key: "71-80",  label: "71–80", min: 71, max: 80 },
  { key: "81-90",  label: "81–90", min: 81, max: 90 },
  { key: "90+",    label: "90+",   min: 90, max: 150 },
];

export default function Obituaries() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({ sources: [], countries: [], states: [] });
  const [loading, setLoading] = useState(true);
  const [layout, setLayout] = useState("grid"); // grid | list

  const q = params.get("q") || "";
  const country = params.get("country") || "";
  const state = params.get("state") || "";
  const city = params.get("city") || "";
  const source = params.get("source") || "";
  const sinceDays = params.get("since_days") || "";
  const ageKey = params.get("age") || "";
  const ageRange = AGE_RANGES.find((r) => r.key === ageKey) || AGE_RANGES[0];

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v) next.delete(k);
    else next.set(k, v);
    setParams(next);
  };
  const clearAll = () => setParams(new URLSearchParams());

  useEffect(() => {
    http.get("/obituaries/facets").then((r) => setFacets(r.data || { sources: [], countries: [], states: [] })).catch(() => {});
    // Fetch featured — 8 newest photo-bearing obits with english_snippet
    http.get("/obituaries", { params: { limit: 40 } }).then((r) => {
      const rich = (r.data.items || [])
        .filter((m) => m.portrait && m.english_snippet && m.english_snippet.length > 40)
        .slice(0, 8);
      setFeatured(rich);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const qs = {
      q: q || undefined,
      country: country || undefined,
      state: state || undefined,
      city: city || undefined,
      source: source || undefined,
      since_days: sinceDays || undefined,
      age_min: ageRange.min ?? undefined,
      age_max: ageRange.max ?? undefined,
      limit: 120,
    };
    http.get("/obituaries", { params: qs })
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [q, country, state, city, source, sinceDays, ageKey]);

  const grouped = useMemo(() => {
    const g = new Map();
    for (const m of items) {
      const iso = m.created_at || m.imported_at || "";
      const day = iso.slice(0, 10) || "unknown";
      if (!g.has(day)) g.set(day, []);
      g.get(day).push(m);
    }
    return Array.from(g.entries());
  }, [items]);

  const anyFilter = q || country || state || city || source || sinceDays || ageKey;

  return (
    <div data-testid="obituaries-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
      <SeoHead section="obituaries" url="https://socialpulse.com/obituaries" />
      <ObituariesTabs position="top" />
      {/* Hero */}
      <div className="overline mb-3" style={{ color: "var(--wofy-primary)" }}>OBITUARIES ARCHIVE</div>
      <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
        Every life, remembered — <br />
        <span style={{ color: "var(--wofy-primary)" }}>with warmth, in one place.</span>
      </h1>
      <p className="mt-4 text-base md:text-lg max-w-2xl leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
        Every photo-bearing obituary we've imported from partner sources, newest
        first, grouped by the day we discovered them. Each card links back to
        the original — and you can leave a candle, a memory, or an anniversary
        subscription in seconds.
      </p>

      {/* Featured carousel */}
      {featured.length > 0 && (
        <section className="mt-10" data-testid="obit-featured">
          <div className="flex items-baseline justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
              <h2 className="font-display text-2xl">Today's featured obituaries</h2>
            </div>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-4 -mx-1 px-1 snap-x snap-mandatory scrollbar-thin">
            {featured.map((m) => (
              <FeaturedCard key={m.id} m={m} />
            ))}
          </div>
        </section>
      )}

      {/* Filter bar */}
      <div className="mt-10 wofy-card">
        <form
          className="flex gap-2 mb-4"
          onSubmit={(e) => { e.preventDefault(); setParam("q", e.currentTarget.q.value); }}
        >
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
            <input
              name="q"
              data-testid="obituaries-search"
              defaultValue={q}
              placeholder="Search by name, hometown, snippet…"
              className="w-full rounded-full border pl-10 pr-4 py-3 text-sm bg-transparent"
              style={{ borderColor: "var(--wofy-border)" }}
            />
          </div>
          <button className="wofy-btn-primary" data-testid="obituaries-search-btn">Search</button>
        </form>

        <FilterRow label="When">
          {[
            { v: "", l: "All time" },
            { v: "1", l: "Today" },
            { v: "7", l: "This week" },
            { v: "30", l: "This month" },
            { v: "90", l: "Last 90 days" },
          ].map((opt) => (
            <Chip key={opt.v} active={sinceDays === opt.v} onClick={() => setParam("since_days", opt.v)} testid={`obit-when-${opt.v || 'all'}`}>{opt.l}</Chip>
          ))}
        </FilterRow>

        <FilterRow label="Age">
          {AGE_RANGES.map((r) => (
            <Chip key={r.key || "any"} active={ageKey === r.key} onClick={() => setParam("age", r.key)} testid={`obit-age-${r.key || 'any'}`}>
              {r.label}
            </Chip>
          ))}
        </FilterRow>

        {facets.sources.length > 0 && (
          <FilterRow label="Source">
            <Chip active={!source} onClick={() => setParam("source", "")} testid="obit-source-all">All sources</Chip>
            {facets.sources.map((s) => (
              <Chip key={s.source} active={source === s.source} onClick={() => setParam("source", s.source)} testid={`obit-source-${s.source}`}>
                {s.attribution || s.source} <span className="opacity-60">({s.count})</span>
              </Chip>
            ))}
          </FilterRow>
        )}

        {facets.countries.length > 0 && (
          <FilterRow label="Country">
            <Chip active={!country} onClick={() => setParam("country", "")} testid="obit-country-all">Everywhere</Chip>
            {facets.countries.slice(0, 10).map((c) => (
              <Chip key={c.country} active={country === c.country} onClick={() => setParam("country", c.country)} testid={`obit-country-${(c.country || 'unknown').toLowerCase()}`}>
                {c.country || "Unknown"} <span className="opacity-60">({c.count})</span>
              </Chip>
            ))}
          </FilterRow>
        )}

        {country && facets.states.filter((s) => s.country === country).length > 0 && (
          <FilterRow label="State / region">
            <Chip active={!state} onClick={() => setParam("state", "")} testid="obit-state-all">Everywhere</Chip>
            {facets.states.filter((s) => s.country === country).slice(0, 12).map((s) => (
              <Chip key={s.state} active={state === s.state} onClick={() => setParam("state", s.state)} testid={`obit-state-${(s.state || 'unknown').toLowerCase()}`}>
                {s.state} <span className="opacity-60">({s.count})</span>
              </Chip>
            ))}
          </FilterRow>
        )}

        {/* Bottom row: layout toggle + trust count + clear */}
        <div className="mt-4 flex items-center justify-between flex-wrap gap-3 pt-3 border-t" style={{ borderColor: "var(--wofy-border)" }}>
          <div className="flex items-center gap-2 text-xs" style={{ color: "var(--wofy-text-soft)" }} data-testid="obit-trust-count">
            <span className="font-display text-lg font-medium" style={{ color: "var(--wofy-text)" }}>{total.toLocaleString()}</span>
            {total === 1 ? "obituary" : "obituaries"} remembered
            {anyFilter && (
              <button type="button" onClick={clearAll} data-testid="obituaries-clear-filters" className="ml-2 underline underline-offset-4">
                <X className="w-3 h-3 inline mr-0.5" /> clear filters
              </button>
            )}
          </div>
          <div className="flex items-center gap-1 rounded-full border p-1" style={{ borderColor: "var(--wofy-border)" }} data-testid="obit-layout-toggle">
            <button
              type="button"
              onClick={() => setLayout("grid")}
              data-testid="obit-layout-grid"
              className={`px-3 py-1 rounded-full inline-flex items-center gap-1.5 text-xs transition-colors ${layout === "grid" ? "text-white" : "opacity-70 hover:opacity-100"}`}
              style={layout === "grid" ? { background: "var(--wofy-primary)" } : {}}
              aria-label="Grid layout"
            >
              <LayoutGrid className="w-3 h-3" /> Grid
            </button>
            <button
              type="button"
              onClick={() => setLayout("list")}
              data-testid="obit-layout-list"
              className={`px-3 py-1 rounded-full inline-flex items-center gap-1.5 text-xs transition-colors ${layout === "list" ? "text-white" : "opacity-70 hover:opacity-100"}`}
              style={layout === "list" ? { background: "var(--wofy-primary)" } : {}}
              aria-label="List layout"
            >
              <List className="w-3 h-3" /> List
            </button>
          </div>
        </div>
      </div>

      {/* Mid-page email opt-in — appears between filters and results
          so casual scrollers see it without leaving the page. */}
      <ObituarySubscribeStrip country={country || null} state={state || null} source="obituaries-archive" />

      {/* Results — grouped by day */}
      <div className="mt-8">
        {loading ? (
          <div className="flex items-center gap-2 py-16 justify-center text-sm" style={{ color: "var(--wofy-text-soft)" }}>
            <Loader2 className="w-4 h-4 animate-spin" /> Loading obituaries…
          </div>
        ) : items.length === 0 ? (
          <div className="wofy-card text-center py-16">
            <Flower className="w-8 h-8 opacity-40 mx-auto" />
            <div className="font-display text-2xl mt-3">No matching obituaries yet</div>
            <p className="mt-2 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
              Try clearing filters, or check back after the next nightly sync.
            </p>
          </div>
        ) : (
          grouped.map(([day, group]) => (
            <section key={day} className="mb-12" data-testid={`obit-day-${day}`}>
              <div className="flex items-baseline justify-between border-b pb-2 mb-5" style={{ borderColor: "var(--wofy-border)" }}>
                <h2 className="font-display text-xl">{formatDayLabel(day)}</h2>
                <span className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>{group.length} listing{group.length === 1 ? "" : "s"}</span>
              </div>
              {layout === "grid" ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {group.map((m) => <ObitGridCard key={m.id} m={m} />)}
                </div>
              ) : (
                <div className="space-y-3">
                  {group.map((m) => <ObitListCard key={m.id} m={m} />)}
                </div>
              )}
            </section>
          ))
        )}
      </div>

      <ObituariesTabs position="bottom" />
    </div>
  );
}

// ─────────────────────── Sub-components ───────────────────────

// Decides which memorial-creation CTA to show under a card.
// Scraped obits ("external_obituary") get "Create a Memorial →" and pre-fill
// the /obituaries/submit form with the deceased's known details.
// Anything already family-created (has memorial_type=death_announcement,
// followers, tributes, life story, etc.) is treated as "already-enhanced"
// and shown with "Update and Enhance this Memorial →" pointing at the
// full memorial detail page where the family can add candles/photos/bio.
function memorialCtaFor(m) {
  const isScraped = (m.memorial_type || "external_obituary") === "external_obituary";
  // Priority 1 — a family has already submitted a full memorial that
  // was linked back to this obituary board. Point the CTA straight at
  // that enhanced page.
  if (m.enhanced_memorial_slug || m.enhanced_memorial_id) {
    return {
      label: "Update and Enhance this Memorial",
      icon: RefreshCw,
      to: `/celebrate/${m.enhanced_memorial_slug || m.enhanced_memorial_id}`,
      testid: `obit-enhance-${m.id}`,
      subtle: false,
    };
  }
  const isEnhanced =
    !isScraped ||
    (m.follower_count || 0) > 0 ||
    (m.tribute_count || 0) > 0 ||
    (m.candle_count || 0) > 0 ||
    (m.short_bio && m.short_bio.length > 40) ||
    (m.life_story && m.life_story.length > 40) ||
    !!m.memorial_card_url;

  if (isEnhanced) {
    return {
      label: "Update and Enhance this Memorial",
      icon: RefreshCw,
      to: `/celebrate/${m.slug || m.id}`,
      testid: `obit-enhance-${m.id}`,
      subtle: false,
    };
  }
  // Pre-fill the submit form with everything we already know so the
  // family (or a friend) only has to add the personal touches.
  const params = new URLSearchParams();
  if (m.full_name) params.set("name", m.full_name);
  if (m.preferred_name && m.preferred_name !== m.full_name) params.set("known_as", m.preferred_name);
  if (m.age_at_death) params.set("age", String(m.age_at_death));
  if (m.death_date) params.set("death_date", String(m.death_date).slice(0, 10));
  if (m.birth_date) params.set("birth_date", String(m.birth_date).slice(0, 10));
  if (m.location?.city) params.set("city", m.location.city);
  if (m.location?.state) params.set("state", m.location.state);
  if (m.location?.country) params.set("country", m.location.country);
  if (m.portrait) params.set("portrait", m.portrait);
  if (m.english_snippet) params.set("text", m.english_snippet);
  if (m.source_url) params.set("source_url", m.source_url);
  if (m.id) params.set("source_id", m.id);
  return {
    label: "Create a Memorial",
    icon: Feather,
    to: `/obituaries/submit?${params.toString()}`,
    testid: `obit-create-${m.id}`,
    subtle: true,
  };
}

// Open the original obituary in a new tab when a source_url exists,
// otherwise fall back to our internal memorial page. This is what the
// user expects when they click the portrait or headline of a scraped
// obituary card — go to the full obituary at the source.
function ObitCardHead({ m, children }) {
  if (m.source_url) {
    return (
      <a
        href={m.source_url}
        target="_blank"
        rel="noopener noreferrer nofollow"
        data-testid={`obit-source-${m.id}`}
        className="block"
        title={`Open the full obituary at ${m.source_attribution || m.source || "source"}`}
      >
        {children}
      </a>
    );
  }
  return (
    <Link to={`/celebrate/${m.slug || m.id}`} className="block">
      {children}
    </Link>
  );
}

function MemorialCta({ m }) {
  const cta = memorialCtaFor(m);
  const Icon = cta.icon;
  return (
    <Link
      to={cta.to}
      onClick={(e) => e.stopPropagation()}
      data-testid={cta.testid}
      className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 w-full justify-center transition-colors"
      style={
        cta.subtle
          ? { background: "rgba(139, 92, 246, 0.08)", color: "#7C3AED", border: "1px solid rgba(139, 92, 246, 0.25)" }
          : { background: "var(--wofy-primary)", color: "#fff" }
      }
    >
      <Icon className="w-3.5 h-3.5" />
      {cta.label}
      <ChevronRight className="w-3.5 h-3.5" />
    </Link>
  );
}

function FeaturedCard({ m }) {
  return (
    <div
      data-testid={`obit-featured-${m.id}`}
      className="shrink-0 w-[320px] snap-start rounded-2xl overflow-hidden border hover:shadow-lg transition-shadow group flex flex-col"
      style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-card, #fff)" }}
    >
      <ObitCardHead m={m}>
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={m.portrait}
            alt={m.name_latin || m.full_name || "Memorial portrait"}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
          {m.source_url && (
            <span className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/50 text-white/95 text-[10px] font-mono uppercase tracking-wider px-2 py-1">
              <ExternalLink className="w-3 h-3" /> source
            </span>
          )}
          <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
            <div className="font-display text-xl leading-tight drop-shadow">
              {m.name_latin || m.preferred_name || m.full_name}
            </div>
            <div className="text-[11px] opacity-90 mt-0.5 flex items-center gap-1.5">
              {m.age_at_death ? <span>Age {m.age_at_death}</span> : null}
              {m.location?.city ? (<><span className="opacity-60">·</span><MapPin className="w-3 h-3" />{m.location.city}</>) : null}
            </div>
          </div>
        </div>
        <p className="px-4 py-3 text-sm leading-snug line-clamp-3" style={{ color: "var(--wofy-text-soft)" }}>
          {m.english_snippet}
        </p>
      </ObitCardHead>
      <div className="px-4 pb-4 mt-auto">
        <MemorialCta m={m} />
        <div className="mt-2 text-[10px] font-mono uppercase tracking-wide opacity-60">
          via {m.source_attribution || m.source}
        </div>
      </div>
    </div>
  );
}

function ObitGridCard({ m }) {
  return (
    <div className="wofy-card block hover:shadow-md transition-shadow overflow-hidden group relative" data-testid={`obit-card-${m.id}`}>
      <ObitCardHead m={m}>
        {m.portrait ? (
          <div className="relative">
            <img src={m.portrait} alt={m.name_latin || m.full_name || "Portrait"} loading="lazy" className="w-full aspect-square object-cover rounded-xl mb-3" />
            {m.source_url && (
              <span className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/50 text-white/95 text-[10px] font-mono uppercase tracking-wider px-2 py-1">
                <ExternalLink className="w-3 h-3" /> source
              </span>
            )}
          </div>
        ) : (
          <div className="w-full aspect-square rounded-xl mb-3 bg-neutral-100 flex items-center justify-center">
            <Flower className="w-8 h-8 opacity-30" />
          </div>
        )}
        <div className="font-display text-lg leading-tight truncate">
          {m.name_latin || m.preferred_name || m.full_name}
        </div>
        {m.name_latin && m.full_name && m.name_latin !== m.full_name && (
          <div className="text-xs opacity-70 mt-0.5 truncate">{m.full_name}</div>
        )}
        {(m.verification_badges?.length > 0) && (
          <MemorialBadges
            badges={m.verification_badges}
            size="sm"
            className="mt-1.5"
            testidPrefix={`obit-grid-badge-${m.id}`}
          />
        )}
        <div className="mt-2 text-xs flex items-center gap-1" style={{ color: "var(--wofy-text-soft)" }}>
          {m.age_at_death ? <span>Age {m.age_at_death}</span> : null}
          {m.age_at_death && m.location?.city ? <span>·</span> : null}
          {m.location?.city ? <span className="inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" />{m.location.city}</span> : null}
        </div>
        {m.english_snippet && (
          <p className="mt-2 text-sm leading-snug line-clamp-2" style={{ color: "var(--wofy-text-soft)" }}>
            {m.english_snippet}
          </p>
        )}
      </ObitCardHead>
      <MemorialCta m={m} />
      <CardActions m={m} />
      <div className="mt-3 text-[10px] font-mono uppercase tracking-wide opacity-60">
        via {m.source_attribution || m.source}
      </div>
    </div>
  );
}

function ObitListCard({ m }) {
  return (
    <div className="wofy-card flex gap-4 items-start" data-testid={`obit-card-${m.id}`}>
      <div className="shrink-0">
        <ObitCardHead m={m}>
          {m.portrait ? (
            <img src={m.portrait} alt={m.name_latin || m.full_name || "Portrait"} loading="lazy" className="w-24 h-24 md:w-32 md:h-32 object-cover rounded-xl" />
          ) : (
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-xl bg-neutral-100 flex items-center justify-center">
              <Flower className="w-8 h-8 opacity-30" />
            </div>
          )}
        </ObitCardHead>
      </div>
      <div className="flex-1 min-w-0">
        <ObitCardHead m={m}>
          <div className="font-display text-xl leading-tight inline-flex items-center gap-1.5 flex-wrap">
            <span>{m.name_latin || m.preferred_name || m.full_name}</span>
            {m.source_url && (
              <ExternalLink className="w-3.5 h-3.5 opacity-40" />
            )}
          </div>
          {m.name_latin && m.full_name && m.name_latin !== m.full_name && (
            <div className="text-xs opacity-70 mt-0.5">{m.full_name}</div>
          )}
          {(m.verification_badges?.length > 0) && (
            <MemorialBadges
              badges={m.verification_badges}
              size="sm"
              className="mt-1.5"
              testidPrefix={`obit-list-badge-${m.id}`}
            />
          )}
          <div className="mt-1 text-xs flex items-center gap-1 flex-wrap" style={{ color: "var(--wofy-text-soft)" }}>
            {m.age_at_death ? <span>Age {m.age_at_death}</span> : null}
            {m.age_at_death && m.location?.city ? <span>·</span> : null}
            {m.location?.city ? <span className="inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" />{m.location.city}</span> : null}
            <span className="opacity-60">·</span>
            <span className="text-[10px] font-mono uppercase tracking-wide">{m.source_attribution || m.source}</span>
          </div>
          {m.english_snippet && (
            <p className="mt-2 text-sm leading-snug line-clamp-2" style={{ color: "var(--wofy-text-soft)" }}>
              {m.english_snippet}
            </p>
          )}
        </ObitCardHead>
        <MemorialCta m={m} />
        <div className="mt-2">
          <CardActions m={m} />
        </div>
      </div>
    </div>
  );
}

// Card-inline actions — Legacy-style but rewritten in our warmer voice.
// Primary "Leave a memory" jumps to the memorial detail page + focuses
// the guestbook. Follow ❤ toggles the memorial follow via the existing
// API. Share tries the Web Share API, falls back to copying the link.
function CardActions({ m }) {
  const detailUrl = `/celebrate/${m.slug || m.id}`;
  const [followed, setFollowed] = useState(!!m.is_following);
  const [busy, setBusy] = useState(false);

  const toggleFollow = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setBusy(true);
    try {
      const { data } = await http.post(`/memorials/${m.slug || m.id}/follow`);
      setFollowed(!!data?.is_following);
      toast.success(data?.is_following ? "Following — you'll be notified on anniversaries" : "Unfollowed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Please sign in to follow this memorial.");
    } finally {
      setBusy(false);
    }
  };

  const share = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}${detailUrl}`;
    const shareData = { title: m.full_name || "In loving memory", url };
    if (navigator.share) {
      try { await navigator.share(shareData); return; } catch { /* fall through */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch { toast.error("Couldn't copy link"); }
  };

  return (
    <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid={`obit-card-actions-${m.id}`}>
      <Link
        to={`${detailUrl}#leave-a-memory`}
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs transition-colors"
        style={{ background: "var(--wofy-primary)", color: "#fff" }}
        data-testid={`obit-card-memory-${m.id}`}
        onClick={(e) => e.stopPropagation()}
      >
        <PenLine className="w-3.5 h-3.5" /> Leave a memory
      </Link>
      {/* "Sign the guestbook" alias — familiar phrasing, same target. */}
      <Link
        to={`${detailUrl}#leave-a-memory`}
        className="hidden md:inline-flex items-center rounded-full border px-2.5 py-1.5 text-xs hover:bg-neutral-50 transition-colors"
        style={{ borderColor: "var(--wofy-border)" }}
        data-testid={`obit-card-guestbook-${m.id}`}
        onClick={(e) => e.stopPropagation()}
      >
        Sign guestbook
      </Link>
      <button
        type="button"
        onClick={toggleFollow}
        disabled={busy}
        aria-pressed={followed}
        className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1.5 text-xs hover:bg-neutral-50 transition-colors"
        style={{ borderColor: "var(--wofy-border)", color: followed ? "#DC2626" : "inherit" }}
        data-testid={`obit-card-follow-${m.id}`}
        title={followed ? "Unfollow" : "Follow — get notified on anniversaries"}
      >
        <Heart className={`w-3.5 h-3.5 ${followed ? "fill-current" : ""}`} />
        {followed ? "Following" : "Follow"}
      </button>
      <button
        type="button"
        onClick={share}
        className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1.5 text-xs hover:bg-neutral-50 transition-colors"
        style={{ borderColor: "var(--wofy-border)" }}
        data-testid={`obit-card-share-${m.id}`}
        title="Share"
      >
        <Share2 className="w-3.5 h-3.5" />
        <span className="sr-only">Share</span>
      </button>
    </div>
  );
}

function FilterRow({ label, children }) {
  return (
    <div className="flex flex-wrap items-center gap-2 py-2">
      <span className="text-[10px] font-mono uppercase tracking-[0.18em] w-20 shrink-0" style={{ color: "var(--wofy-text-soft)" }}>
        {label}
      </span>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs transition-colors ${active ? "text-white" : "hover:bg-neutral-50"}`}
      style={active
        ? { background: "var(--wofy-primary)", borderColor: "var(--wofy-primary)" }
        : { borderColor: "var(--wofy-border)" }}
    >
      {children}
    </button>
  );
}

function formatDayLabel(day) {
  if (!day || day === "unknown") return "Date unknown";
  try {
    const d = new Date(day + "T00:00:00Z");
    return d.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  } catch {
    return day;
  }
}
